/**
 * Versandrouting Stufe 1 (R112–R124), zwei kleine Seiten unter Einstellungen:
 *  - /einstellungen/versand: Tarife read-only (Konten, Optionen, Tarifklassen, Zuschläge, Präferenzen) mit Quelle und
 *    Unsicherheit; Regeln Verzug, Selbsteinlieferung, Spitzentag (Admin ändert, Audit).
 *  - /einstellungen/abholungen: Abholaufträge als Statusobjekt, manuell gepflegt (Leitung), Vorschlag Zusatzabholung.
 * Nichts davon ändert den heutigen Versandweg: VERSANDROUTING läuft in Stufe 1 nur im Schattenbetrieb.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ABHOLUNG_ARTEN, ABHOLUNG_STATUS_TEXT, ABHOLUNG_UEBERGAENGE, centsAlsText,
  type Abholauftrag, type AbholungArt, type AbholungStatus, type SpitzentagVerhalten, type Versandregeln,
} from '@storeos/contracts';
import { abholungAnlegen, abholungen, abholungStatus, versandRegelnAendern, versandTarife } from '../api/versandrouting';
import { datum, euro } from '../components/format';
import { Darf, Dialog, DialogFormular, Feld, Karte, Seitenkopf, Tabelle, useToast, Zustand } from '../components/ui';

const zurueck = <Link to="/einstellungen" className="knopf-zweit klein">Zurück zu Einstellungen</Link>;
const quelle = (q: string | null, unsicher: boolean) => <small className="hinweis">{unsicher && <strong>unsicher · </strong>}{q ?? 'ohne Quelle'}</small>;
const gueltig = (von: string, bis: string | null) => `${datum(von)} – ${bis ? datum(bis) : 'offen'}`;
const SPITZENTAG_TEXT: Record<SpitzentagVerhalten, string> = { vorschlagen: 'nur vorschlagen', beauftragen: 'beauftragen (folgt mit der Pickup-Anbindung, bis dahin wie vorschlagen)' };
const EINLIEFERUNG_TEXT: Record<string, string> = { abholung: 'Abholung', filiale: 'Filiale', paketshop: 'PaketShop', briefkasten: 'Briefkasten', grossannahme: 'Großannahme' };
const ABHOLUNG_KONTO_TEXT: Record<string, string> = { keine: 'keine', regel: 'Regelabholung', auf_abruf: 'auf Abruf' };
const ART_TEXT: Record<AbholungArt, string> = { bedarf: 'Bedarfsabholung', einmal: 'Einmalabholung', zusatz: 'Zusatzabholung' };

export function VersandTarifeSeite() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['versand-tarife'], queryFn: versandTarife });
  const [edit, setEdit] = useState(false);
  const d = q.data;
  const kontoName = (id: string) => d?.tarife.konten.find((k) => k.id === id)?.name ?? '–';
  const optionName = (id: string | null) => (id ? d?.tarife.optionen.find((o) => o.id === id)?.name ?? '–' : 'alle Optionen des Kontos');
  return (
    <section>
      <Seitenkopf titel="Versand / Tarife" unter="Tarifdaten aus den DHL-Rechnungen und Angeboten, je Zeile mit Quelle. Stufe 1: StoreOS rechnet nur im Schattenbetrieb, der Versand läuft unverändert.">{zurueck}</Seitenkopf>
      <Zustand laden={q.isLoading} fehler={q.error}>
        {d && (
          <div>
            <Karte titel="Schalter und Regeln" aktion={<Darf rolle="admin"><button type="button" className="knopf-zweit klein" onClick={() => setEdit(true)} data-testid="versandregeln-bearbeiten">Bearbeiten</button></Darf>}>
              <dl className="werte">
                <dt>VERSANDROUTING</dt><dd data-testid="versandrouting-schalter">{d.schalter.konfiguriert} <small className="hinweis">wirksam: {d.schalter.wirksam}{d.schalter.hinweis ? ` · ${d.schalter.hinweis}` : ''}</small></dd>
                <dt>Verzug je Werktag</dt><dd data-testid="regel-verzug">{euro(d.regeln.verzug_cents_je_werktag)} <small className="hinweis">Voreinstellung 0,50 €, noch nicht final entschieden</small></dd>
                <dt>Selbsteinlieferung</dt><dd data-testid="regel-selbsteinlieferung">{d.regeln.selbsteinlieferung_erlaubt ? 'erlaubt' : 'aus'} <small className="hinweis">Standard aus: keine Kapazität für Filialgänge</small></dd>
                <dt>Spitzentag</dt><dd data-testid="regel-spitzentag">Zusatzabholung ab {d.regeln.abholung_zusatz_ab_paketen} Paketen: {SPITZENTAG_TEXT[d.regeln.spitzentag_verhalten]}</dd>
                <dt>Weitere Regeln</dt><dd className="hinweis">Tracking Pflicht: {d.regeln.tracking_pflicht ? 'ja' : 'nein'} · Selbstversicherung bis {euro(d.regeln.selbstversicherung_bis_cents)} · höchstens {d.regeln.laufzeitverlust_max_werktage} Werktag(e) später als die früheste Abholung · erwartete Packzeit {d.regeln.erwartete_packzeit} Uhr</dd>
              </dl>
            </Karte>
            <Karte titel="Konten">
              <Tabelle zeilen={d.tarife.konten} schluessel={(k) => k.id} testid="tabelle-konten"
                spalten={[
                  { kopf: 'Konto', zelle: (k) => <>{k.name} <small className="hinweis mono">{k.code}</small></> },
                  { kopf: 'Abholung', zelle: (k) => `${ABHOLUNG_KONTO_TEXT[k.abholung] ?? k.abholung}${k.abholung_vorlauf_werktage ? `, ${k.abholung_vorlauf_werktage} Werktag Vorlauf` : ''}` },
                  { kopf: 'Grundgebühr/Monat', zelle: (k) => euro(k.grundgebuehr_cents), rechts: true },
                  { kopf: 'Gültig', zelle: (k) => gueltig(k.gueltig_von, k.gueltig_bis) },
                  { kopf: 'Aktiv', zelle: (k) => (k.aktiv ? 'ja' : 'nein') },
                  { kopf: 'Quelle / Hinweis', zelle: (k) => <>{quelle(k.quelle, k.unsicher)}{k.hinweis && <><br /><small className="hinweis">{k.hinweis}</small></>}</> },
                ]} />
            </Karte>
            <Karte titel="Optionen">
              <Tabelle zeilen={d.tarife.optionen} schluessel={(o) => o.id} testid="tabelle-optionen"
                spalten={[
                  { kopf: 'Option', zelle: (o) => <>{o.name} <small className="hinweis mono">{o.produkt}</small></> },
                  { kopf: 'Konto', zelle: (o) => kontoName(o.konto_id) },
                  { kopf: 'Einlieferung', zelle: (o) => `${EINLIEFERUNG_TEXT[o.einlieferung] ?? o.einlieferung}${o.abholung_von ? ` ${o.abholung_von}` : ''}, Annahmeschluss ${o.annahmeschluss}` },
                  { kopf: 'Abholung bestätigt', zelle: (o) => (o.abholung_bestaetigt ? 'ja' : <strong>nein</strong>) },
                  { kopf: 'Grenzen', zelle: (o) => `${o.max_g ? `${(o.max_g / 1000).toLocaleString('de-DE')} kg` : '–'}${o.max_l_mm ? ` · ${o.max_l_mm} × ${o.max_b_mm} × ${o.max_h_mm} mm` : ''}` },
                  { kopf: 'Haftung', zelle: (o) => (o.haftung_cents === null ? 'unbekannt' : euro(o.haftung_cents)), rechts: true },
                  { kopf: 'Aktiv', zelle: (o) => (o.aktiv ? 'ja' : 'nein') },
                  { kopf: 'Quelle / Hinweis', zelle: (o) => <>{quelle(o.quelle, o.unsicher)}{o.hinweis && <><br /><small className="hinweis">{o.hinweis}</small></>}</> },
                ]} />
            </Karte>
            <Karte titel="Tarifklassen (je Sendung; DHL netto laut Rechnung)">
              <Tabelle zeilen={d.tarife.klassen} schluessel={(c) => c.id} testid="tabelle-klassen"
                spalten={[
                  { kopf: 'Option', zelle: (c) => optionName(c.option_id) },
                  { kopf: 'Klasse', zelle: (c) => c.name },
                  { kopf: 'Preis', zelle: (c) => euro(c.preis_cents), rechts: true },
                  { kopf: 'Gültig', zelle: (c) => gueltig(c.gueltig_von, c.gueltig_bis) },
                  { kopf: 'Quelle', zelle: (c) => quelle(c.quelle, c.unsicher) },
                ]} />
            </Karte>
            <Karte titel="Zuschläge">
              <Tabelle zeilen={d.tarife.zuschlaege} schluessel={(z) => z.id} testid="tabelle-zuschlaege"
                spalten={[
                  { kopf: 'Zuschlag', zelle: (z) => z.name },
                  { kopf: 'Konto', zelle: (z) => `${kontoName(z.konto_id)}${z.option_id ? ` · ${optionName(z.option_id)}` : ''}` },
                  { kopf: 'Betrag', zelle: (z) => (z.art === 'fix' ? euro(z.betrag_cents ?? 0) : `${centsAlsText(z.basispunkte ?? 0)} % aufs Porto`), rechts: true },
                  { kopf: 'Wann', zelle: (z) => (z.bedingung === 'zieltyp_packstation' ? 'Packstation' : z.bedingung === 'zieltyp_postfiliale' ? 'Postfiliale' : z.bedingung === 'zeitraum' ? 'Zeitraum' : 'immer') },
                  { kopf: 'Gültig', zelle: (z) => gueltig(z.gueltig_von, z.gueltig_bis) },
                  { kopf: 'Quelle', zelle: (z) => quelle(z.quelle, z.unsicher) },
                ]} />
            </Karte>
            <Karte titel="Präferenzen">
              <Tabelle zeilen={d.tarife.praeferenzen} schluessel={(p) => p.id} leer="Keine Präferenzen hinterlegt (vorbereitet, z. B. Geschäftskunden per UPS)."
                spalten={[
                  { kopf: 'Option', zelle: (p) => optionName(p.option_id) },
                  { kopf: 'Bedingung', zelle: (p) => `${p.bedingung} = ${p.wert}` },
                  { kopf: 'Wirkung', zelle: (p) => (p.wirkung === 'ausschluss' ? 'Ausschluss' : euro(p.betrag_cents ?? 0)) },
                  { kopf: 'Grund', zelle: (p) => p.grund },
                  { kopf: 'Gültig', zelle: (p) => gueltig(p.gueltig_von, p.gueltig_bis) },
                ]} />
            </Karte>
          </div>
        )}
      </Zustand>
      <Dialog offen={edit} titel="Versandregeln bearbeiten" onSchliessen={() => setEdit(false)}>
        {edit && d && <RegelnFormular r={d.regeln} onAbbrechen={() => setEdit(false)} onFertig={() => { setEdit(false); toast('Regeln gespeichert'); void qc.invalidateQueries({ queryKey: ['versand-tarife'] }); }} />}
      </Dialog>
    </section>
  );
}

function RegelnFormular({ r, onAbbrechen, onFertig }: { r: Versandregeln; onAbbrechen: () => void; onFertig: () => void }) {
  const [verzug, setVerzug] = useState(centsAlsText(r.verzug_cents_je_werktag));
  const [selbst, setSelbst] = useState(r.selbsteinlieferung_erlaubt);
  const [spitze, setSpitze] = useState<SpitzentagVerhalten>(r.spitzentag_verhalten);
  const [ab, setAb] = useState(String(r.abholung_zusatz_ab_paketen));
  return (
    <DialogFormular absendenText="Speichern" onAbbrechen={onAbbrechen} onAbsenden={async () => {
      const cents = Math.round(Number(verzug.replace(',', '.')) * 100);
      if (!Number.isFinite(cents) || cents < 0) throw new Error('Verzug als Eurobetrag ≥ 0 angeben');
      await versandRegelnAendern({ verzug_cents_je_werktag: cents, selbsteinlieferung_erlaubt: selbst, spitzentag_verhalten: spitze, abholung_zusatz_ab_paketen: Number(ab) });
      onFertig();
    }}>
      <Feld label="Verzug je Werktag (€)" hinweis="Was ein Werktag späterer Versand in der Kostenrechnung kostet. 0 = reine Kostenentscheidung.">
        <input value={verzug} onChange={(x) => setVerzug(x.target.value)} inputMode="decimal" data-testid="feld-verzug" />
      </Feld>
      <Feld label="Selbsteinlieferung erlauben" hinweis="Standard aus. An = Optionen mit Filiale/PaketShop werden zulässig (nur, wenn jemand die Pakete hinbringt).">
        <input type="checkbox" checked={selbst} onChange={(x) => setSelbst(x.target.checked)} data-testid="feld-selbsteinlieferung" />
      </Feld>
      <Feld label="Zusatzabholung an Spitzentagen">
        <select value={spitze} onChange={(x) => setSpitze(x.target.value as SpitzentagVerhalten)} data-testid="feld-spitzentag">
          <option value="vorschlagen">{SPITZENTAG_TEXT.vorschlagen}</option>
          <option value="beauftragen">{SPITZENTAG_TEXT.beauftragen}</option>
        </select>
      </Feld>
      <Feld label="Vorschlag ab Paketen am Tag"><input value={ab} onChange={(x) => setAb(x.target.value)} inputMode="numeric" /></Feld>
    </DialogFormular>
  );
}

export function AbholungenSeite() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['abholungen'], queryFn: () => abholungen() });
  const [neu, setNeu] = useState(false);
  const [aendern, setAendern] = useState<{ a: Abholauftrag; nach: AbholungStatus } | null>(null);
  const neuLaden = () => void qc.invalidateQueries({ queryKey: ['abholungen'] });
  return (
    <section>
      <Seitenkopf titel="Abholaufträge" unter="Zusatz- und Einmalabholungen, manuell gepflegt: beim Carrier im Portal oder per Telefon beauftragen, hier Status und Referenz eintragen. Die DHL-Regelabholung 10:30 braucht keinen Auftrag.">
        <span className="knopfreihe">
          {zurueck}
          <button type="button" className="knopf klein" onClick={() => setNeu(true)} data-testid="abholung-neu">Neuer Abholauftrag</button>
        </span>
      </Seitenkopf>
      <Zustand laden={q.isLoading} fehler={q.error}>
        {q.data && (
          <>
            <Karte titel={`Heute (${datum(q.data.heute.tag)})`}>
              <p data-testid="abholung-heute">{q.data.heute.pakete} Lagersendung(en) zwischen „bereit“ und „Label erstellt“.</p>
              {q.data.heute.vorschlag.vorschlagen ? <p className="warn-box" data-testid="abholung-vorschlag">{q.data.heute.vorschlag.text}</p> : <p className="hinweis">Kein Vorschlag für eine Zusatzabholung.</p>}
            </Karte>
            <Karte titel="Aufträge (letzte 30 Tage und später)">
              <Tabelle zeilen={q.data.eintraege} schluessel={(a) => a.id} testid="tabelle-abholungen" leer="Noch keine Abholaufträge."
                spalten={[
                  { kopf: 'Tag', zelle: (a) => datum(a.tag) },
                  { kopf: 'Fenster', zelle: (a) => `${a.fenster_von}–${a.fenster_bis}` },
                  { kopf: 'Konto', zelle: (a) => a.konto_name },
                  { kopf: 'Art', zelle: (a) => ART_TEXT[a.art] },
                  { kopf: 'Pakete', zelle: (a) => a.anzahl_pakete, rechts: true },
                  { kopf: 'Status', zelle: (a) => <span className="badge neutral" data-testid="abholung-status">{ABHOLUNG_STATUS_TEXT[a.status]}</span> },
                  { kopf: 'Referenz / Grund', zelle: (a) => <small className="hinweis">{[a.carrier_referenz, a.storno_grund, a.fehler, a.bemerkung].filter(Boolean).join(' · ') || '–'}</small> },
                  {
                    kopf: '', zelle: (a) => (
                      <span className="knopfreihe">
                        {ABHOLUNG_UEBERGAENGE[a.status].map((nach) => (
                          <button key={nach} type="button" className={nach === 'storniert' ? 'knopf-gefahr klein' : 'knopf-zweit klein'} onClick={() => setAendern({ a, nach })}>{ABHOLUNG_STATUS_TEXT[nach]}</button>
                        ))}
                      </span>
                    ),
                  },
                ]} />
            </Karte>
          </>
        )}
      </Zustand>
      <Dialog offen={neu} titel="Neuer Abholauftrag" onSchliessen={() => setNeu(false)}>
        {neu && q.data && <NeuFormular konten={q.data.konten} tag={q.data.heute.tag} pakete={q.data.heute.pakete} onAbbrechen={() => setNeu(false)} onFertig={() => { setNeu(false); toast('Abholauftrag angelegt (geplant)'); neuLaden(); }} />}
      </Dialog>
      <Dialog offen={!!aendern} titel={aendern ? `Status: ${ABHOLUNG_STATUS_TEXT[aendern.nach]}` : ''} onSchliessen={() => setAendern(null)}>
        {aendern && <StatusFormular a={aendern.a} nach={aendern.nach} onAbbrechen={() => setAendern(null)} onFertig={() => { setAendern(null); toast('Status gespeichert'); neuLaden(); }} />}
      </Dialog>
    </section>
  );
}

function NeuFormular({ konten, tag, pakete, onAbbrechen, onFertig }: { konten: Array<{ id: string; name: string }>; tag: string; pakete: number; onAbbrechen: () => void; onFertig: () => void }) {
  const [konto, setKonto] = useState(konten[0]?.id ?? '');
  const [t, setT] = useState(tag);
  const [von, setVon] = useState('08:00');
  const [bis, setBis] = useState('12:00');
  const [art, setArt] = useState<AbholungArt>(pakete > 10 ? 'einmal' : 'zusatz');
  const [anzahl, setAnzahl] = useState(String(Math.max(pakete, 1)));
  const [bem, setBem] = useState('');
  return (
    <DialogFormular absendenText="Anlegen" onAbbrechen={onAbbrechen} onAbsenden={async () => {
      await abholungAnlegen({ konto_id: konto, tag: t, fenster_von: von, fenster_bis: bis, art, anzahl_pakete: Number(anzahl), bemerkung: bem || null });
      onFertig();
    }}>
      <Feld label="Konto"><select value={konto} onChange={(x) => setKonto(x.target.value)}>{konten.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}</select></Feld>
      <div className="raster-felder">
        <Feld label="Tag"><input type="date" value={t} onChange={(x) => setT(x.target.value)} /></Feld>
        <Feld label="Fenster von"><input value={von} onChange={(x) => setVon(x.target.value)} /></Feld>
        <Feld label="Fenster bis"><input value={bis} onChange={(x) => setBis(x.target.value)} /></Feld>
      </div>
      <div className="raster-felder">
        <Feld label="Art" hinweis="DHL: Bedarfsabholung bis 10 Pakete, darüber Einmalabholung"><select value={art} onChange={(x) => setArt(x.target.value as AbholungArt)}>{ABHOLUNG_ARTEN.map((a) => <option key={a} value={a}>{ART_TEXT[a]}</option>)}</select></Feld>
        <Feld label="Pakete"><input value={anzahl} onChange={(x) => setAnzahl(x.target.value)} inputMode="numeric" /></Feld>
      </div>
      <Feld label="Bemerkung"><input value={bem} onChange={(x) => setBem(x.target.value)} /></Feld>
    </DialogFormular>
  );
}

function StatusFormular({ a, nach, onAbbrechen, onFertig }: { a: Abholauftrag; nach: AbholungStatus; onAbbrechen: () => void; onFertig: () => void }) {
  const [ref, setRef] = useState(a.carrier_referenz ?? '');
  const [text, setText] = useState('');
  return (
    <DialogFormular absendenText="Speichern" gefahr={nach === 'storniert'} onAbbrechen={onAbbrechen} onAbsenden={async () => {
      await abholungStatus(a.id, { status: nach, carrier_referenz: ref || null, storno_grund: nach === 'storniert' ? text : null, fehler: nach === 'fehlgeschlagen' ? text : null });
      onFertig();
    }}>
      <p>{a.konto_name}, {datum(a.tag)} {a.fenster_von}–{a.fenster_bis}: {ABHOLUNG_STATUS_TEXT[a.status]} → {ABHOLUNG_STATUS_TEXT[nach]}</p>
      {(nach === 'beauftragt' || nach === 'bestaetigt') && <Feld label="Referenz beim Carrier (Auftragsnummer)"><input value={ref} onChange={(x) => setRef(x.target.value)} /></Feld>}
      {nach === 'storniert' && <Feld label="Grund (Pflicht)"><input value={text} onChange={(x) => setText(x.target.value)} /></Feld>}
      {nach === 'fehlgeschlagen' && <Feld label="Was hat der Carrier gemeldet? (Pflicht)"><input value={text} onChange={(x) => setText(x.target.value)} /></Feld>}
    </DialogFormular>
  );
}
